import { getAuthHint, getCurrentRole, getCurrentUser, getRoleHint } from './auth';
import { isSubsRole } from './roles';

// Джерело, яке відкриває вкладка "Каталог" у гостя (заміна "Огляду" — див.
// нижче GUEST_BROWSE_ITEM). Змінити тут, якщо гостьовим джерелом за
// замовчуванням має стати інше.
export const GUEST_SOURCE_ID = 'smakolykytl';

const ITEMS = [
  {
    key: 'library',
    href: '/library',
    label: 'Бібліотека',
    icon: '<path d="M4 4h4v16H4zM11 4h4v16h-4zM18 5l3 1-4 15-3-1z"/>',
    fill: true,
  },
  {
    key: 'updates',
    href: '/updates',
    label: 'Нове',
    icon: '<path d="M12 2 1 21h22L12 2zm0 6v6m0 3h.01" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    fill: false,
  },
  {
    key: 'history',
    href: '/history',
    label: 'Історія',
    icon: '<path d="M3 12a9 9 0 1 0 3-6.7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 4v5h5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 7v5l3 3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    fill: false,
  },
  {
    key: 'browse',
    href: '/browse',
    label: 'Огляд',
    icon: '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M15 9l-2 6-6 2 2-6z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    fill: false,
  },
  {
    key: 'more',
    href: '/more',
    label: 'Більше',
    icon: '<circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>',
    fill: true,
  },
];

// Без входу доступні лише ці дві вкладки — решта (Бібліотека/Нове/Історія) —
// особисті дані і показуються лише авторизованим.
const PUBLIC_KEYS = new Set(['browse', 'more']);

// У гостя вкладка "browse" веде не на список усіх джерел (/browse), а одразу
// відкриває каталог GUEST_SOURCE_ID на головній (/) — ключ той самий ('browse'), тож підсвітка
// активного пункту (item.key === active) працює без додаткових умов.
const GUEST_BROWSE_ITEM: (typeof ITEMS)[number] = {
  key: 'browse',
  href: '/', // головна гостя — каталог (server: routes/pretty.ts); адреси /plugin/<id> гість не бачить
  label: 'Каталог',
  icon: '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M15 9l-2 6-6 2 2-6z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  fill: false,
};

type NavKind = 'guest' | 'sub' | 'user';

/** Набір вкладок за типом користувача: гість — Каталог+Більше; гостьовий акаунт — усе, але "Каталог" замість "Огляду"; звичайний — усе. */
function itemsFor(kind: NavKind): typeof ITEMS {
  if (kind === 'user') return ITEMS;
  if (kind === 'sub') return ITEMS.map((i) => (i.key === 'browse' ? GUEST_BROWSE_ITEM : i));
  return ITEMS.filter((i) => PUBLIC_KEYS.has(i.key)).map((i) => (i.key === 'browse' ? GUEST_BROWSE_ITEM : i));
}

function kindFromHint(): NavKind {
  if (getAuthHint() !== 'user') return 'guest';
  return isSubsRole(getRoleHint()) ? 'sub' : 'user';
}

function paintNav(mount: HTMLElement, active: string, items: typeof ITEMS) {
  mount.innerHTML = items
    .map(
      (item) => `
    <a href="${item.href}" class="navitem ${item.key === active ? 'active' : ''}">
      <svg viewBox="0 0 24 24" class="icon" ${item.fill ? 'fill="currentColor"' : 'fill="none"'}>${item.icon}</svg>
      <span>${item.label}</span>
    </a>`,
    )
    .join('');
  forceRepaint(mount);
}

/**
 * Нижнє меню — окремий композитний шар (CSS: translateZ/will-change). Після підміни його вмісту мобільні браузери
 * інколи не перемальовують шар, доки сторінку не поворушиш ("меню з'являється лише після прокрутки"). Мікро-зміна
 * прозорості на один кадр змушує перемалювати шар одразу; візуально непомітна.
 */
function forceRepaint(el: HTMLElement) {
  const kick = () => {
    if (!el.isConnected) return;
    // Скидаємо композитний шар і створюємо наново: саме це браузер робить при першій прокрутці.
    el.style.willChange = 'auto';
    el.style.transform = 'none';
    void el.offsetHeight; // синхронний reflow
    requestAnimationFrame(() => {
      el.style.willChange = '';
      el.style.transform = '';
      void el.offsetHeight;
    });
  };
  kick();
  requestAnimationFrame(() => requestAnimationFrame(kick));
  // Сторінка добудовується (каталог, шрифти, адресний рядок) — повторюємо, поки макет не втихомириться.
  setTimeout(kick, 120);
  setTimeout(kick, 400);
  // PWA: додаткові спроби + штучна подія resize (на Android вона змушує перерахувати фіксовані шари).
  if (window.matchMedia?.('(display-mode: standalone), (display-mode: fullscreen), (display-mode: minimal-ui)').matches) {
    for (const ms of [800, 1500]) setTimeout(kick, ms);
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
  }
  if (document.readyState !== 'complete') window.addEventListener('load', kick, { once: true });
  window.addEventListener('pageshow', kick, { once: true });
}

export function renderNav(active: string) {
  const mount = document.getElementById('bottomnav');
  if (!mount) return;
  // Одразу малюємо правильний набір пунктів за підказкою (getAuthHint/getRoleHint: результат
  // попереднього завантаження). Невідомо (найперший візит) — вважаємо гостем: коротко не показати
  // авторизованому зайві вкладки краще, ніж гостю — чужі особисті. Коли прийде справжня відповідь
  // про вхід, перемальовуємо лише якщо вона відрізняється.
  const painted = kindFromHint();
  paintNav(mount, active, itemsFor(painted));
  getCurrentUser().then(async (username) => {
    const role = username ? await getCurrentRole() : null;
    const actual: NavKind = !username ? 'guest' : isSubsRole(role) ? 'sub' : 'user';
    if (actual !== painted) paintNav(mount, active, itemsFor(actual));
  });
}
